import { Action } from 'redux'

export function assign<T>(target : any, source  : T, source2 : T) : T {
	return Object.assign(target,source,source2)
}

export enum AsyncStatus {
    CREATED, LOADING, ERROR, UPDATING, ERROR_UPDATING, COMPLETED
}
export interface AsyncState {
    /**
     * The current status of the state
     */
    status? : AsyncStatus;
    lastReceived? : number;
    lastRequested? : number;
}

export interface ReduxAction<T> extends Action {
    payload: T;
    error?: boolean;
    meta?: any;
    createdTime: number;
}
export function createAction<T>(type: any, payload?: T) {
	return {
		type,
		payload: payload && Object.keys(payload).length ? payload : undefined,
		createdTime: Date.now(),
	}
}

export function hasExpired(forced : boolean, state : AsyncState, expiresSec : number) {
	if (forced === true) {
		return true
	} else if (state.status === AsyncStatus.UPDATING || state.status === AsyncStatus.LOADING) {
		return false
	} else if (!state.lastReceived) {
		return true
	}
	return Date.now() - state.lastReceived >= expiresSec * 1000
}

export function handleAsyncError(state : AsyncState) : AsyncState {
	return assign<AsyncState>({}, state,{
		status : state.status == AsyncStatus.LOADING ? AsyncStatus.ERROR : AsyncStatus.ERROR_UPDATING
	})
}

/**
 * Checks if you should display loading indicator in UI components, 
 * returns true if the status is CREATED or LOADING
 * @param state
 * @returns {boolean}
 */
export function isLoading<T extends AsyncState>(state : T) {
	if (state.status === AsyncStatus.CREATED || state.status === AsyncStatus.LOADING) {
		return true
	} else {
		return false
	}
}

/**
 * Returns true if the status is UPDATING
 */
export function isUpdating<T extends AsyncState>(state: T) {
	return state.status === AsyncStatus.UPDATING
}

/**
 * Checks whether the state has a fatal error due to failed async request.
 * Fatal error means that the state has never had a successful response from
 * async task (server request)
 * @param state
 * @returns {boolean}
 */
export function hasFatalError(state : AsyncState) {
	if (state.status === AsyncStatus.ERROR) {
		return true
	} else {
		return false
	}
}

export function handleAsyncRequest(state : AsyncState, action : ReduxAction<any>) {
	return assign<AsyncState>({}, state,{
		status : state.status == AsyncStatus.CREATED ? AsyncStatus.LOADING : AsyncStatus.UPDATING,
		lastRequested : action.createdTime
	})
}

export function createAsyncState<T>(state : T) {
	return Object.assign({},state, { status : AsyncStatus.CREATED })
}

/**
 * Returns an existing promise if multiple requests are attempted before the original promise has resolved.
 * @param makeRequest - factory function to create the promise.
 */
export function preventDuplicateRequest<T>(makeRequest: () => Promise<T>): () => Promise<T> {
	let promise: Promise<T> | undefined
	return () => {
		if (promise) return promise

		promise = makeRequest()
		promise.catch(() => {}).then(() => promise = undefined)

		return promise
	}
}